/**
 * incidentCopy — plain-English names for an incident's severity and status.
 *
 * The Reports "Incidents" card is where a non-technical founder scans for
 * "what broke, and how bad is it?". Each incident carries a `severity`
 * (`low | medium | high | critical`) and a `status` (`open | ack | resolved`).
 * Those are engineer words: a founder reading a chip that says "high" or,
 * worse, "ack" can't tell whether an issue is urgent or already handled —
 * "ack" (acknowledged) is invisible jargon to someone who's never triaged a
 * ticket. Every other founder-facing chip in HQ speaks plainly (runLabels,
 * nodeStatus, approvalCopy); this card was the last one leaking raw enums.
 *
 * `severityLabel` / `incidentStatusLabel` map each value to one short,
 * escalating, readable phrase; `*Tip` gives the one-line hover/accessible
 * explanation. Anything unrecognised falls back to a de-slugged Title-case
 * string, so a new enum value never leaks a raw slug to a founder.
 *
 * Kept dependency-free (string keys, no @shared/types import) so it is
 * unit-tested directly (tests/incident-copy.test.ts).
 */

/** severity code → short chip phrase, an escalating plain-English ladder. */
const SEVERITY_LABELS: Record<string, string> = {
  low: 'Minor',
  medium: 'Moderate',
  high: 'Serious',
  critical: 'Critical',
};

/** severity code → one-line plain explanation (hover title + accessible label). */
const SEVERITY_TIPS: Record<string, string> = {
  low: 'Minor — worth noting, not urgent.',
  medium: 'Moderate — should be looked at soon.',
  high: 'Serious — needs attention.',
  critical: 'Critical — drop everything and fix.',
};

/** incident status code → short chip phrase a founder can read. */
const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  ack: 'Acknowledged',
  resolved: 'Resolved',
};

/** incident status code → one-line plain explanation. */
const STATUS_TIPS: Record<string, string> = {
  open: 'Open — not looked at yet.',
  ack: 'Acknowledged — seen, fix not confirmed yet.',
  resolved: 'Resolved — fixed and verified.',
};

/** De-slug an unknown code: 'foo-bar' → 'Foo bar'. */
function deslug(s: string): string {
  const words = s.replace(/[-_]+/g, ' ').trim();
  if (!words) return s;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Short, founder-readable name for an incident's severity. */
export function severityLabel(sev: string | null | undefined): string {
  if (!sev) return 'Unknown';
  const key = sev.trim();
  return SEVERITY_LABELS[key] ?? deslug(key);
}

/** One-line explanation for a severity, for tooltips and accessible labels. */
export function severityTip(sev: string | null | undefined): string {
  if (!sev) return 'Severity unknown.';
  const key = sev.trim();
  return SEVERITY_TIPS[key] ?? `Severity: ${deslug(key)}.`;
}

/** Short, founder-readable name for an incident's status. */
export function incidentStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  const key = status.trim();
  return STATUS_LABELS[key] ?? deslug(key);
}

/** One-line explanation for an incident status, for tooltips and accessible labels. */
export function incidentStatusTip(status: string | null | undefined): string {
  if (!status) return 'Status unknown.';
  const key = status.trim();
  return STATUS_TIPS[key] ?? `Status: ${deslug(key)}.`;
}
